import { useEffect, useRef, useState } from 'react';
import { CameraScanner } from './CameraScanner';

export type ScanKind = 'lot' | 'product' | 'location' | 'bin';

const KIND: Record<ScanKind, { label: string; icon: string; placeholder: string }> = {
  lot: { label: 'LOT LABEL', icon: '🏷️', placeholder: 'Scan or type the lot label number' },
  product: { label: 'PRODUCT BARCODE', icon: '📦', placeholder: 'Scan the product barcode' },
  location: { label: 'LOCATION', icon: '📍', placeholder: 'Scan or type a location code' },
  bin: { label: 'BIN LABEL', icon: '🧺', placeholder: 'Scan or type the bin label (BIN…)' },
};

/**
 * One input per kind of code, colour-coded, so a number scanned into the
 * lot-label box can never be taken as a product barcode (or vice versa).
 * Works with keyboard-wedge USB/Bluetooth scanners (they type, then press Enter),
 * manual typing, and the phone camera.
 */
export function ScanField({
  kind,
  onScan,
  autoFocus,
  disabled,
  busy,
  label,
}: {
  kind: ScanKind;
  onScan: (code: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  busy?: boolean;
  label?: string;
}) {
  const [value, setValue] = useState('');
  const [camera, setCamera] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const k = KIND[kind];

  useEffect(() => {
    if (autoFocus && !disabled) inputRef.current?.focus();
  }, [autoFocus, disabled]);

  const submit = (code: string) => {
    const c = code.trim();
    if (!c || busy) return;
    onScan(c);
    setValue('');
  };

  return (
    <div className={`scan scan-${kind}`}>
      <div className="scan-kind">
        <span aria-hidden>{k.icon}</span> {label ?? k.label}
      </div>
      <form
        className="scan-row"
        onSubmit={(e) => {
          e.preventDefault();
          submit(value);
        }}
      >
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={k.placeholder}
          inputMode="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          disabled={disabled}
          aria-label={label ?? k.label}
        />
        <button className="btn" type="submit" disabled={disabled || busy || !value.trim()}>
          {busy ? '…' : 'Go'}
        </button>
        <button className="btn btn-ghost" type="button" disabled={disabled} onClick={() => setCamera(true)} title="Scan with camera" aria-label="Scan with camera">
          📷
        </button>
      </form>
      {camera && (
        <CameraScanner
          title={`Scan ${k.label.toLowerCase()}`}
          onResult={(code) => {
            setCamera(false);
            submit(code);
          }}
          onClose={() => setCamera(false)}
        />
      )}
    </div>
  );
}
